import cronstrue from "cronstrue";
import { differenceInMinutes } from "date-fns";

/**
 * Business rule: Job runs are considered timed out if they've been
 * IN_PROGRESS with no updates for this many minutes.
 */
export const JOB_RUN_TIMEOUT_MINUTES = 45;

/**
 * Check if a job run has timed out (IN_PROGRESS with no updates for 45+ minutes).
 * @param status - The job run status
 * @param updatedAt - The last update timestamp (ISO string)
 * @param currentTime - Optional current time for stable rendering (avoids new Date() on each call)
 */
export function isJobRunTimedOut(status: string, updatedAt: string, currentTime?: number): boolean {
  if (status !== "IN_PROGRESS" || !updatedAt) return false;
  try {
    const now = currentTime ? new Date(currentTime) : new Date();
    const minutesSinceUpdate = differenceInMinutes(now, new Date(updatedAt));
    return minutesSinceUpdate >= JOB_RUN_TIMEOUT_MINUTES;
  } catch {
    return false;
  }
}

/**
 * Get the effective status of a job run, treating timed out jobs as ERRORED.
 * @param status - The job run status
 * @param updatedAt - The last update timestamp (ISO string)
 * @param currentTime - Optional current time for stable rendering
 */
export function getEffectiveJobRunStatus(status: string, updatedAt: string, currentTime?: number): string {
  return isJobRunTimedOut(status, updatedAt, currentTime) ? "ERRORED" : status;
}

/**
 * Format a cron expression into a human-readable string using cronstrue.
 * Falls back to the raw cron expression if parsing fails.
 * @param cronExpression - The cron expression to format
 * @returns Human-readable schedule string with "(UTC)" suffix, or the raw cron if parsing fails
 */
export function formatCronExpression(cronExpression: string): string {
  if (!cronExpression?.trim()) {
    return "";
  }

  try {
    return `${cronstrue.toString(cronExpression, { verbose: true })} (UTC)`;
  } catch {
    return cronExpression;
  }
}
